import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { crm, uploadCrmDoc, uploadCrmFile } from './crmApi';
import type { CrmAuth, CrmFile, CrmLead, CrmMe, MeetingPick, Outcome, PitchResult } from './crmApi';
import { SlotPicker } from './Meetings';
import { HolidayWarning, Modal, fmtPhone, fmtWhen, fromLocalInput, holidayProblem, inputCls, labelCls, offHolidays, purpleBg, toLocalInput } from './ui';

// ── Log call: what happened on the call ──────────────────────
// Not Picked · Call Later · Cold Lead · Brochure Sent (the form + recording,
// then Poll Sent / Future Prospect / Direct Visit) · Coordinator Meeting ·
// Converted. A Not Picked asks what they heard: twice "didn't ring" (switched
// off, not reachable…) on a lead nobody has talked to and that never rang
// sends it to Junk Leads; once it rang, it goes the usual way (3 tries each).
// A Poll Sent asks when the poll went out; its status is due a few
// hours later (PollModal). A Coordinator Meeting asks what the coordinator
// needs to know about the student and books one of the Student Coordinators'
// free times (Meetings.tsx); it is not a brochure (the coordinator sends that
// after the meeting). A P2P Meeting (Path to Placement) asks the student's
// name, email ID (every P2P email goes there), degree and passed-out year, and
// is booked for any day and any time, now included; a Lead Manager takes it,
// and the resume must be on the lead within 30 minutes (by the meeting, if
// sooner). On a Direct Visit's day, "Didn't come" alone puts the lead in No
// Response.
// Mirrors the app's crm_log_call_screen.dart.

const OUTCOMES: { key: Outcome; hint: string }[] = [
  { key: 'Not Picked',    hint: 'Pick when to try again' },
  { key: 'Call Later',    hint: 'They asked for another time' },
  { key: 'Cold Lead',     hint: 'Picked up, not a real lead' },
  { key: 'Brochure Sent', hint: 'Form + recording + result' },
  { key: 'Coordinator Meeting', hint: 'Book a Student Coordinator' },
  { key: 'P2P Meeting',   hint: 'Path to Placement · a Lead Manager' },
  { key: 'Converted',     hint: 'They paid' },
];
const MAX_RESUME_MB = 5;
function resumeProblem(f: File): string {
  if (!/\.(pdf|docx?)$/i.test(f.name)) return `${f.name} is not a resume. Pick a PDF or Word file (.pdf, .doc, .docx).`;
  if (f.size > MAX_RESUME_MB * 1048576) return `${f.name} is ${(f.size / 1048576).toFixed(1)} MB. The most is ${MAX_RESUME_MB} MB.`;
  if (!f.size) return `${f.name} is empty. Pick it again.`;
  return '';
}
const RESULTS: { key: PitchResult; hint: string; when: string }[] = [
  { key: 'Poll Sent',       hint: 'You sent them the poll', when: 'Poll sent at' },
  { key: 'Future Prospect', hint: 'Not now, maybe later',   when: 'Call them again on' },
  { key: 'Direct Visit',    hint: 'They will come in',      when: 'Visit on' },
];
const PITCH_FIELDS: [keyof PitchForm, string][] = [['name', 'Name'], ['degree', 'Degree'], ['passedOutYear', 'Passed-out year'], ['profile', 'Profile'], ['location', 'Location'], ['course', 'Course'], ['mode', 'Online or offline']];
// What a coordinator meeting asks
const MEETING_FIELDS: [keyof PitchForm, string][] = [['name', 'Name'], ['passedOutYear', 'Passed-out year'], ['degree', 'Degree'], ['profile', 'Student profile']];
type PitchForm = { name: string; degree: string; passedOutYear: string; profile: string; location: string; course: string; notes: string; mode: string };

// Not Picked "try again" (retryHours): that long from now, or the next working
// morning when that falls outside working hours
function laterInWorkHours(work: CrmMe['work'], hours: number): string {
  const at = new Date(Date.now() + hours * 3600e3);
  if (!work || !work.days.length) return toLocalInput(at);
  const hm = (s: string) => { const [h, m] = s.split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  const start = hm(work.start), end = hm(work.end);
  const ist = new Date(at.getTime() + 330 * 60000);
  const mins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
  const isWorkDay = (d: Date) => work.days.includes(d.getUTCDay());
  if (isWorkDay(ist) && mins >= start && mins < end) return toLocalInput(at);
  let day = new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()));
  if (!(isWorkDay(day) && mins < start)) {
    for (let i = 0; i < 14; i++) { day = new Date(day.getTime() + 86400e3); if (isWorkDay(day)) break; }
  }
  return `${day.toISOString().slice(0, 10)}T${String(Math.floor(start / 60)).padStart(2, '0')}:${String(start % 60).padStart(2, '0')}`;
}
// (never on a holiday: those move to the next working day)
const retryDefault = (me: CrmMe) => offHolidays(me, laterInWorkHours(me.work, me.work?.retryHours || 2));
const minutesAgo = (m: number) => toLocalInput(new Date(Date.now() - m * 60000));
// Quick poll times: gone already, or later (5:30 PM is the time most use, while
// it is still ahead; the next working day's start). The box takes any other.
function pollChips(me: CrmMe): [string, string][] {
  const at530 = new Date();
  at530.setHours(17, 30, 0, 0);
  const morning = me.work?.pollMorning;
  return [
    ['Just now', minutesAgo(0)], ['15 min ago', minutesAgo(15)], ['30 min ago', minutesAgo(30)], ['1 hour ago', minutesAgo(60)],
    ['In 1 hour', minutesAgo(-60)],
    ...(at530.getTime() > Date.now() + 5 * 60000 ? [['5:30 PM today', toLocalInput(at530)] as [string, string]] : []),
    ...(morning ? [[fmtWhen(morning), toLocalInput(morning)] as [string, string]] : []),
  ];
}
const fromNow = (me: CrmMe, ms: number) => offHolidays(me, toLocalInput(new Date(Date.now() + ms)));
// A P2P meeting (any day, any time): about an hour from now, on the half hour
function p2pDefault(): string {
  const d = new Date(Date.now() + 3600e3);
  d.setMinutes(Math.ceil(d.getMinutes() / 30) * 30, 0, 0);
  return toLocalInput(d);
}
const P2P_BOOK_DAYS = 14;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Tomorrow at hh:00 (a holiday too: P2P meetings are any day)
function tomorrowAt(hh: number): string {
  const now = new Date();
  return toLocalInput(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, hh, 0));
}

// A call recording: any audio file the phone saved (.m4a, .mp3, .amr…). The
// picker shows every file, because some phones hide .m4a behind an audio
// filter; the server reads the file itself to be sure it is audio.
const MAX_RECORDING_MB = 60;
function recordingProblem(f: File): string {
  const ext = f.name.toLowerCase().split('.').pop() || '';
  if (f.type.startsWith('image/') || f.type.startsWith('text/') || ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'jpg', 'jpeg', 'png', 'heic', 'webp', 'apk', 'zip'].includes(ext)) {
    return `${f.name} is not a call recording. Pick the audio file (.m4a, .mp3, .amr…).`;
  }
  if (f.size > MAX_RECORDING_MB * 1048576) return `${f.name} is ${(f.size / 1048576).toFixed(0)} MB. The most is ${MAX_RECORDING_MB} MB.`;
  if (!f.size) return `${f.name} is empty. Pick the recording again.`;
  return '';
}

const Chips: React.FC<{ items: string[]; value: string; onPick: (v: string) => void; hints?: Record<string, string>; label: string }> = ({ items, value, onPick, hints, label }) => (
  <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
    {items.map(i => (
      <button key={i} type="button" role="radio" aria-checked={value === i} onClick={() => onPick(i)}
        className="px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all text-left"
        style={value === i ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' } : { background: '#fff', color: '#4b5563', borderColor: '#e5e7eb' }}>
        {i}
        {hints?.[i] && <span className="block text-[10px] font-medium opacity-70">{hints[i]}</span>}
      </button>
    ))}
  </div>
);

const ReasonPicker: React.FC<{ list: string[]; value: string; other: string; onValue: (v: string) => void; onOther: (v: string) => void }> = ({ list, value, other, onValue, onOther }) => (
  <div className="space-y-2">
    <select value={value} onChange={e => onValue(e.target.value)} className={inputCls} aria-label="Reason">
      <option value="">— Pick a reason —</option>
      {list.map(r => <option key={r} value={r}>{r}</option>)}
      {!list.includes('Other') && <option value="Other">Other</option>}
    </select>
    {value === 'Other' && <input value={other} onChange={e => onOther(e.target.value)} placeholder="Type the reason" className={inputCls} aria-label="The reason" />}
  </div>
);

const LogCallModal: React.FC<{ auth: CrmAuth; lead: CrmLead; me: CrmMe; initialOutcome?: Outcome; onClose: () => void; onSaved: (lead: CrmLead) => void }> = ({ auth, lead, me, initialOutcome, onClose, onSaved }) => {
  const [outcome, setOutcome] = useState<Outcome | ''>(initialOutcome || '');
  const [visit, setVisit]     = useState<'' | 'came' | 'no-show'>('');
  const [note, setNote]       = useState('');
  const [when, setWhen]       = useState(() => fromNow(me, 60 * 60000));
  const [pollAt, setPollAt]   = useState(minutesAgo(0));
  const [reason, setReason]   = useState('');
  const [other, setOther]     = useState('');
  const [heard, setHeard]     = useState('');
  const [result, setResult]   = useState<PitchResult | ''>(lead.pitched && ['Future Prospect', 'Direct Visit'].includes(lead.stage) ? lead.stage as PitchResult : '');
  const [pitch, setPitch]     = useState<PitchForm>({
    name: lead.name, degree: lead.degree, passedOutYear: lead.passedOutYear, profile: lead.profile,
    location: lead.location, course: lead.course, notes: lead.pitchNotes, mode: lead.mode || '',
  });
  // A brochure sent before: the form is filled in already, so it starts folded
  const [showForm, setShowForm] = useState(!lead.pitched);
  const [recording, setRecording] = useState<File | null>(null);
  const [amount, setAmount]   = useState('1000');
  const [shot, setShot]       = useState<File | null>(null);
  const [meeting, setMeeting] = useState<MeetingPick | null>(null);
  const [p2pAt, setP2pAt]     = useState(p2pDefault);
  const [email, setEmail]     = useState(lead.email || '');   // a P2P meeting's: every P2P email goes there
  const [resume, setResume]   = useState<File | null>(null);
  const [saving, setSaving]   = useState('');
  const hasResume = !!lead.resume;
  const resumeMinutes = me.p2p?.resumeMinutes || 30;
  // The visit's day and they didn't come: no outcome, the lead goes to No Response
  const missed = !!lead.visitDue && visit === 'no-show';
  // A file goes up once: saving again after an error reuses it
  const uploaded = useRef<{ file: File; ref: CrmFile }[]>([]);
  const setP = (k: keyof PitchForm, v: string) => setPitch(p => ({ ...p, [k]: v }));
  const finalReason = reason === 'Other' ? other.trim() : reason;
  const courses = me.lists.courses.includes(pitch.course) || !pitch.course ? me.lists.courses : [pitch.course, ...me.lists.courses];
  const pollHours = me.work?.pollHours || 3;
  // Not Picked: what they heard (older servers don't ask)
  const np = me.notPicked;
  const pollDueGuess = pollAt ? new Date(new Date(fromLocalInput(pollAt)).getTime() + pollHours * 3600e3).toISOString() : '';

  const upload = async (kind: 'recording' | 'screenshot', file: File) => {
    const done = uploaded.current.find(u => u.file === file);
    if (done) return done.ref;
    setSaving(kind === 'recording' ? 'Uploading the recording…' : 'Uploading the screenshot…');
    const ref = await uploadCrmFile(auth, lead.id, kind, file);
    uploaded.current.push({ file, ref });
    return ref;
  };

  // The same checks the server makes, before anything is uploaded
  const problem = (): string => {
    if (missed) return '';
    if (!outcome) return 'Pick what happened on the call';
    const future = () => !!when && new Date(fromLocalInput(when)).getTime() > Date.now() - 5 * 60000;
    if (outcome === 'Not Picked' && np && !heard) return 'Pick what you heard on the call';
    if (outcome === 'Not Picked' && !future()) return 'Pick when to try again (not in the past)';
    if (outcome === 'Call Later' && !future()) return 'Pick a call-back time that is not in the past';
    const datedResult = outcome === 'Brochure Sent' && !!result && result !== 'Poll Sent';
    if ((outcome === 'Not Picked' || outcome === 'Call Later' || datedResult) && holidayProblem(me, when)) return holidayProblem(me, when);
    if (outcome === 'Cold Lead' && !finalReason) return 'Say why they are a cold lead';
    if (outcome === 'Coordinator Meeting') {
      const missing = MEETING_FIELDS.filter(([k]) => !String(pitch[k] || '').trim()).map(([, label]) => label);
      if (missing.length) return `Fill in: ${missing.join(', ')}`;
      if (recording && recordingProblem(recording)) return recordingProblem(recording);
      if (!meeting) return 'Pick a meeting time';
    }
    if (outcome === 'Brochure Sent') {
      const missing = PITCH_FIELDS.filter(([k]) => !String(pitch[k] || '').trim()).map(([, label]) => label);
      if (missing.length) { setShowForm(true); return `Fill in the form: ${missing.join(', ')}`; }
      if (!recording && !lead.pitched) { setShowForm(true); return 'Attach the call recording. It is compulsory the first time a brochure is sent.'; }
      if (recording && recordingProblem(recording)) return recordingProblem(recording);
      if (!result) return 'Pick the result: Poll Sent, Future Prospect or Direct Visit';
      if (result === 'Poll Sent') {
        const t = pollAt ? new Date(fromLocalInput(pollAt)).getTime() : NaN;
        if (!pollAt || isNaN(t)) return 'Pick when the poll was sent';
        if (t < Date.now() - 3 * 86400e3) return 'Pick a poll time in the last 3 days';
      } else if (!future()) return 'Pick a date and time that is not in the past';
    }
    if (outcome === 'Converted' && !(Number(amount) > 0)) return 'Enter the amount they paid';
    if (outcome === 'P2P Meeting') {
      const missing = ([['Student name', pitch.name], ['Email ID', email], ['Degree', pitch.degree], ['Passed-out year', pitch.passedOutYear]] as [string, string][])
        .filter(([, v]) => !String(v || '').trim()).map(([label]) => label);
      if (missing.length) return `Fill in: ${missing.join(', ')}`;
      if (!EMAIL_RE.test(email.trim())) return "Enter the student's email ID (like name@gmail.com)";
      const t = p2pAt ? new Date(fromLocalInput(p2pAt)).getTime() : NaN;
      if (!p2pAt || isNaN(t)) return 'Pick the date and time of the P2P meeting';
      if (t < Date.now() - 5 * 60000) return 'The P2P meeting time is in the past. Pick now or later.';
      if (t > Date.now() + P2P_BOOK_DAYS * 86400e3) return `Book the P2P meeting within the next ${P2P_BOOK_DAYS} days`;
      if (resume && resumeProblem(resume)) return resumeProblem(resume);
    }
    return '';
  };

  const save = async () => {
    const p = problem();
    if (p) { toast.error(p); return; }
    // Didn't come for the visit: nothing else to pick (the server puts it in No Response)
    if (missed) {
      setSaving('Saving…');
      try {
        const r = await crm.logCall(auth, { leadId: lead.id, outcome: '', visit, note: note.trim() });
        toast.success(`${lead.name || fmtPhone(lead.phone)}: didn't come for the visit, now in No Response`);
        onSaved(r.lead);
      } catch (e: any) {
        toast.error(e.message);
      } finally {
        setSaving('');
      }
      return;
    }
    const payload: Record<string, unknown> = { leadId: lead.id, outcome, note: note.trim(), visit };
    try {
      if (outcome === 'Not Picked' || outcome === 'Call Later') payload.nextAt = fromLocalInput(when);
      if (outcome === 'Not Picked' && heard) payload.heard = heard;
      if (outcome === 'Cold Lead') payload.reason = finalReason;
      if (outcome === 'Coordinator Meeting') {
        payload.pitch = { name: pitch.name, passedOutYear: pitch.passedOutYear, degree: pitch.degree, profile: pitch.profile, notes: pitch.notes };
        payload.meeting = meeting;
        if (recording) payload.recording = await upload('recording', recording);
      }
      if (outcome === 'Brochure Sent') {
        payload.pitch = pitch;
        payload.result = result;
        if (result === 'Poll Sent') payload.pollSentAt = fromLocalInput(pollAt);
        else payload.nextAt = fromLocalInput(when);
        if (recording) payload.recording = await upload('recording', recording);
      }
      if (outcome === 'Converted') {
        payload.amount = Number(amount);
        if (shot) payload.screenshot = await upload('screenshot', shot);
      }
      if (outcome === 'P2P Meeting') {
        payload.pitch = { name: pitch.name.trim(), email: email.trim(), degree: pitch.degree.trim(), passedOutYear: pitch.passedOutYear.trim() };
        payload.meetingAt = fromLocalInput(p2pAt);
        // The resume goes on the lead first, so nothing is due after booking
        if (resume && !hasResume && !uploaded.current.some(u => u.file === resume)) {
          setSaving('Uploading the resume…');
          const up = await uploadCrmDoc(auth, lead.id, 'resume', resume);
          uploaded.current.push({ file: resume, ref: up.file });
        }
      }
      setSaving('Saving…');
      const r = await crm.logCall(auth, payload);
      const said = outcome === 'Not Picked' && r.lead.stage === 'Junk Lead' ? `${heard}: didn't ring ${np?.noRingLimit ?? 2} times, so it went to Junk Leads`
        : outcome === 'Not Picked' ? `Not Picked${heard ? ` (${heard})` : ''}, try again ${fmtWhen(r.lead.nextAt)}`
        : outcome === 'Coordinator Meeting' && r.meeting ? `meeting booked ${r.meeting.when}. The coordinators free then hear it; you'll hear when one adds the link.`
        : outcome === 'P2P Meeting' ? `P2P meeting booked ${fmtWhen(fromLocalInput(p2pAt))}. Every Lead Manager hears it; the first to take it has the meeting.${!hasResume && !resume ? ` Add the resume within ${resumeMinutes} minutes.` : ''}`
        : outcome !== 'Brochure Sent' ? outcome : result === 'Poll Sent' ? `poll sent, update it by ${fmtWhen(r.lead.nextAt)}` : result;
      toast.success(`${lead.name || fmtPhone(lead.phone)}: ${said}`);
      onSaved(r.lead);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving('');
    }
  };

  const pickOutcome = (v: Outcome) => {
    if (v !== outcome && v === 'Not Picked') setWhen(retryDefault(me));
    else if (v !== outcome && v === 'Call Later') setWhen(fromNow(me, 60 * 60000));
    setOutcome(v); setReason(''); setOther('');
  };
  const pickResult = (v: PitchResult) => {
    setResult(v);
    if (v === 'Poll Sent') setPollAt(minutesAgo(0));
    else if (!lead.nextAt || new Date(lead.nextAt).getTime() < Date.now()) setWhen(fromNow(me, 24 * 3600e3));
  };

  // A call recording: compulsory on the first brochure, optional for a meeting
  const recordingInput = (id: string, label: string) => (
    <div className="sm:col-span-2 rounded-xl border-2 border-dashed border-purple-200 bg-purple-50/40 p-3">
      <label className={labelCls} htmlFor={id}>{label}</label>
      <input id={id} type="file" onChange={e => {
        const f = e.target.files?.[0] || null;
        if (f && recordingProblem(f)) { toast.error(recordingProblem(f)); e.target.value = ''; setRecording(null); return; }
        setRecording(f);
      }} className="text-sm" />
      {recording && <p className="text-xs text-purple-700 font-semibold mt-1.5">✓ {recording.name} · {(recording.size / 1048576).toFixed(1)} MB</p>}
      <p className="text-[11px] text-gray-400 mt-1.5">Any call recording: .m4a, .mp3, .amr, .aac, .wav… (up to {MAX_RECORDING_MB} MB). Most phones keep them in the Phone app or a "Call recordings" folder.</p>
    </div>
  );

  const pitchForm = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div><label className={labelCls} htmlFor="p-name">Name</label><input id="p-name" value={pitch.name} onChange={e => setP('name', e.target.value)} className={inputCls} /></div>
      <div><label className={labelCls} htmlFor="p-degree">Degree</label><input id="p-degree" value={pitch.degree} onChange={e => setP('degree', e.target.value)} placeholder="B.Com, B.E…" className={inputCls} /></div>
      <div><label className={labelCls} htmlFor="p-year">Passed-out year</label><input id="p-year" value={pitch.passedOutYear} onChange={e => setP('passedOutYear', e.target.value)} placeholder="2024" inputMode="numeric" className={inputCls} /></div>
      <div>
        <label className={labelCls} htmlFor="p-profile">Profile</label>
        <select id="p-profile" value={pitch.profile} onChange={e => setP('profile', e.target.value)} className={inputCls}>
          <option value="">— Pick —</option>
          {me.profiles.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <div><label className={labelCls} htmlFor="p-loc">Location</label><input id="p-loc" value={pitch.location} onChange={e => setP('location', e.target.value)} placeholder="City" className={inputCls} /></div>
      <div>
        <label className={labelCls} htmlFor="p-course">Course</label>
        <select id="p-course" value={pitch.course} onChange={e => setP('course', e.target.value)} className={inputCls}>
          <option value="">— Pick —</option>
          {courses.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <p className={labelCls}>Online or offline classes</p>
        <Chips label="Online or offline" items={['Online', 'Offline']} value={pitch.mode} onPick={v => setP('mode', v)} />
      </div>
      <div className="sm:col-span-2"><label className={labelCls} htmlFor="p-notes">Notes</label><textarea id="p-notes" rows={2} value={pitch.notes} onChange={e => setP('notes', e.target.value)} className={`${inputCls} resize-none`} /></div>
      {recordingInput('p-rec', `Call recording ${lead.pitched ? '(optional now: the brochure went before)' : '(compulsory)'}`)}
    </div>
  );

  // A coordinator meeting: what the coordinator needs to know, and a time
  const meetingForm = (
    <div className="space-y-4">
      <p className="text-xs text-indigo-900 bg-indigo-50 rounded-xl px-3 py-2">The coordinator talks to the student in the meeting and sends the brochure after it. They see these details once they add the meeting link.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className={labelCls} htmlFor="m-name">Name</label><input id="m-name" value={pitch.name} onChange={e => setP('name', e.target.value)} className={inputCls} /></div>
        <div><label className={labelCls} htmlFor="m-year">Passed-out year</label><input id="m-year" value={pitch.passedOutYear} onChange={e => setP('passedOutYear', e.target.value)} placeholder="2024" inputMode="numeric" className={inputCls} /></div>
        <div className="sm:col-span-2"><label className={labelCls} htmlFor="m-degree">Degree</label><input id="m-degree" value={pitch.degree} onChange={e => setP('degree', e.target.value)} placeholder="B.Com, B.E…" className={inputCls} /></div>
        <div className="sm:col-span-2">
          <p className={labelCls}>Student profile</p>
          <Chips label="Student profile" items={me.profiles} value={pitch.profile} onPick={v => setP('profile', v)} />
        </div>
        <div className="sm:col-span-2"><label className={labelCls} htmlFor="m-about">About the student (for the coordinator)</label><textarea id="m-about" rows={2} value={pitch.notes} onChange={e => setP('notes', e.target.value)} className={`${inputCls} resize-none`} placeholder="What they do now, what they are looking for" /></div>
        {recordingInput('m-rec', 'Call recording (optional)')}
      </div>
      <div className="rounded-xl border-2 border-indigo-100 bg-indigo-50/50 p-3 space-y-2">
        <p className={labelCls}>Pick a meeting time</p>
        <SlotPicker auth={auth} value={meeting} onPick={setMeeting} />
      </div>
    </div>
  );

  return (
    <Modal title={`Log call: ${lead.name || fmtPhone(lead.phone)}`} sub={`${fmtPhone(lead.phone)} · now ${lead.stage}`} onClose={onClose} wide>
      <div className="space-y-5">
        {lead.visitDue && (
          <div className="rounded-xl border-2 border-orange-200 bg-orange-50 p-3">
            <p className="text-sm font-bold text-orange-800 mb-2">Did they come for the visit?</p>
            <Chips label="Did they come?" items={['Came', "Didn't come"]} value={visit === 'came' ? 'Came' : visit === 'no-show' ? "Didn't come" : ''}
              onPick={v => { setVisit(v === 'Came' ? 'came' : 'no-show'); if (v !== 'Came') setOutcome(''); }} />
            {missed && (
              <div className="mt-3 space-y-2">
                <p className="text-sm text-gray-700 bg-white rounded-xl px-3 py-2">🔕 This lead goes to <b>No Response</b> ("Didn't come for the visit"). It stays with you: call them again any time from your No Response list.</p>
                <label className={labelCls} htmlFor="crm-note-v">Note</label>
                <textarea id="crm-note-v" rows={2} value={note} onChange={e => setNote(e.target.value)} className={`${inputCls} resize-none`} placeholder="Anything to remember (optional)" />
              </div>
            )}
          </div>
        )}

        {/* (Didn't come: nothing else to pick, so the outcome stays empty) */}
        {!missed && (
          <div>
            <p className={labelCls}>What happened?</p>
            {/* (P2P Meeting: only once the server knows it) */}
            <Chips label="What happened" items={OUTCOMES.filter(o => o.key !== 'P2P Meeting' || !!me.p2p).map(o => o.key)} value={outcome} onPick={v => pickOutcome(v as Outcome)}
              hints={Object.fromEntries(OUTCOMES.map(o => [o.key, o.hint]))} />
          </div>
        )}

        {outcome === 'Not Picked' && (
          <div className="space-y-2">
            {np && (
              <div className="space-y-2">
                <p className={labelCls}>What did you hear?</p>
                <div className="rounded-xl border-2 border-red-100 bg-red-50/40 p-3 space-y-2">
                  <p className="text-[11px] font-bold text-red-800">Didn't ring · {np.noRingLimit} of these → Junk Leads</p>
                  <Chips label="Didn't ring" items={np.noRing} value={heard} onPick={setHeard} />
                </div>
                <div className="rounded-xl border-2 border-gray-100 p-3 space-y-2">
                  <p className="text-[11px] font-bold text-gray-600">Rang · the usual {lead.triesEach ?? 3} tries each</p>
                  <Chips label="Rang" items={np.rang} value={heard} onPick={setHeard} />
                </div>
                {(lead.noRing ?? 0) > 0 && np.noRing.includes(heard) && (
                  <p className="text-xs font-bold text-red-700 bg-red-50 rounded-xl px-3 py-2">
                    It didn't ring {lead.noRing === 1 ? 'once' : `${lead.noRing} times`} before: this call sends it to Junk Leads (if it's 30 minutes or more since that one).
                  </p>
                )}
              </div>
            )}
            <div>
              <label className={labelCls} htmlFor="crm-retry">Try again at</label>
              <input id="crm-retry" type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} className={inputCls} />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[['In 30 min', 0.5], ['In 1 hour', 1], ['In 2 hours', 2], ['In 4 hours', 4]].map(([label, h]) => (
                  <button key={label} type="button" onClick={() => setWhen(toLocalInput(new Date(Date.now() + Number(h) * 3600e3)))}
                    className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-700">{label}</button>
                ))}
              </div>
              <HolidayWarning me={me} value={when} />
            </div>
            <p className="text-xs text-gray-500 bg-gray-50 rounded-xl p-3">
              You'll get a reminder then.{lead.tries != null
                ? ` Each Not Picked is one of your ${lead.triesEach ?? 3} tries (calls under 30 minutes apart count once). After them, or when your shift ends, the lead goes to the next salesperson.`
                : !lead.movesTomorrow ? '' : lead.stage === 'Call Later'
                ? " No brochure sent yet: if that doesn't happen today, the lead moves to the next salesperson tomorrow morning."
                : ' Nobody has picked up yet: if nobody picks up today, the lead moves to the next salesperson tomorrow morning.'}
            </p>
          </div>
        )}

        {outcome === 'Call Later' && (
          <div>
            <label className={labelCls} htmlFor="crm-when">Call back on</label>
            <input id="crm-when" type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} className={inputCls} />
            <HolidayWarning me={me} value={when} />
            {!lead.pitched && <p className="text-xs text-gray-500 bg-gray-50 rounded-xl p-3 mt-2">{lead.tries != null
              ? 'A call-back on another day stays with you till that day. On the call-back day, if no brochure goes, the lead goes to the next salesperson when your shift ends, with this call-back time.'
              : 'If no brochure goes today, the lead moves to the next salesperson tomorrow morning, with this call-back time.'}</p>}
          </div>
        )}

        {outcome === 'Cold Lead' && (
          <div>
            <p className={labelCls}>Why are they a cold lead?</p>
            <ReasonPicker list={me.lists.coldReasons} value={reason} other={other} onValue={setReason} onOther={setOther} />
          </div>
        )}

        {outcome === 'Brochure Sent' && (
          <div className="space-y-4">
            {showForm ? pitchForm : (
              <button type="button" onClick={() => setShowForm(true)} className="text-xs font-bold text-purple-700 hover:underline">✎ Change the details ({pitch.course || 'no course'}, {pitch.profile || 'no profile'}, {pitch.mode || 'online or offline?'})</button>
            )}

            <div>
              <p className={labelCls}>Result</p>
              <Chips label="Result" items={RESULTS.map(r => r.key)} value={result} onPick={v => pickResult(v as PitchResult)}
                hints={Object.fromEntries(RESULTS.map(r => [r.key, r.hint]))} />
            </div>
            {result === 'Poll Sent' && (
              <div className="rounded-xl border-2 border-fuchsia-100 bg-fuchsia-50/50 p-3 space-y-2">
                <label className={labelCls} htmlFor="crm-poll-at">Poll time: when it went out, or will go out (any day)</label>
                <input id="crm-poll-at" type="datetime-local" value={pollAt} onChange={e => setPollAt(e.target.value)} className={inputCls} />
                <div className="flex flex-wrap gap-1.5">
                  {pollChips(me).map(([label, v]) => (
                    <button key={label} type="button" onClick={() => setPollAt(v)}
                      className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-gray-200 bg-white text-gray-600 hover:border-purple-300 hover:text-purple-700">{label}</button>
                  ))}
                </div>
                <p className="text-xs text-fuchsia-900">
                  Update the poll's status within {pollHours} hours of it going out{pollDueGuess ? ` (by about ${fmtWhen(pollDueGuess)})` : ''}. You get a reminder then: Interested, Not interested or No response.
                </p>
              </div>
            )}
            {result && result !== 'Poll Sent' && (
              <div>
                <label className={labelCls} htmlFor="crm-when2">{RESULTS.find(r => r.key === result)?.when}</label>
                <input id="crm-when2" type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} className={inputCls} />
                <HolidayWarning me={me} value={when} />
              </div>
            )}
            {!showForm && (
              <div>
                <label className={labelCls} htmlFor="crm-note-p">Note</label>
                <textarea id="crm-note-p" rows={2} value={note} onChange={e => setNote(e.target.value)} className={`${inputCls} resize-none`} placeholder="What they said" />
              </div>
            )}
          </div>
        )}

        {outcome === 'Coordinator Meeting' && meetingForm}

        {outcome === 'P2P Meeting' && (
          <div className="space-y-4">
            <p className="text-xs text-amber-900 bg-amber-50 rounded-xl px-3 py-2">
              Path to Placement: a {me.p2p?.minutes || 30}-minute online meeting with a Lead Manager. Every Lead Manager hears about it; the first to take it has the meeting and the test fee. Once the fee is paid, a Career Strategist has the student. The student gets the time by email.
            </p>
            {/* The student: every P2P email (the time, the link, the payment links, receipts) goes to this email ID */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={labelCls} htmlFor="p2p-name">Student name</label><input id="p2p-name" value={pitch.name} onChange={e => setP('name', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="p2p-email">Email ID</label><input id="p2p-email" type="email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@gmail.com" className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="p2p-degree">Degree</label><input id="p2p-degree" value={pitch.degree} onChange={e => setP('degree', e.target.value)} placeholder="B.Com, B.E…" className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="p2p-year">Passed-out year</label><input id="p2p-year" value={pitch.passedOutYear} onChange={e => setP('passedOutYear', e.target.value)} placeholder="2024" inputMode="numeric" className={inputCls} /></div>
              <p className="sm:col-span-2 text-[11px] text-gray-400 -mt-1">Check the email ID with the student: the meeting time, the link, the payment links and the receipts all go there.</p>
            </div>
            <div>
              <label className={labelCls} htmlFor="p2p-at">Meeting time</label>
              <input id="p2p-at" type="datetime-local" value={p2pAt} onChange={e => setP2pAt(e.target.value)} className={inputCls} />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {([['Now', () => toLocalInput(new Date())], ['In 30 min', () => toLocalInput(new Date(Date.now() + 30 * 60000))], ['In 1 hour', p2pDefault], ['Tomorrow 11 AM', () => tomorrowAt(11)]] as [string, () => string][]).map(([label, f]) => (
                  <button key={label} type="button" onClick={() => setP2pAt(f())}
                    className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-700">{label}</button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5">Any day and any time: right now, tonight, a Sunday or a holiday (up to {P2P_BOOK_DAYS} days ahead).</p>
            </div>
            <div className={`rounded-xl border-2 p-3 ${hasResume ? 'border-green-100 bg-green-50/60' : 'border-dashed border-amber-300 bg-amber-50/50'}`}>
              {hasResume ? (
                <p className="text-sm text-green-800 font-semibold">✓ The resume is on the lead ({lead.resume!.fileName})</p>
              ) : (
                <>
                  <label className={labelCls} htmlFor="p2p-resume">Resume (PDF or Word)</label>
                  <input id="p2p-resume" type="file" accept=".pdf,.doc,.docx" onChange={e => {
                    const f = e.target.files?.[0] || null;
                    if (f && resumeProblem(f)) { toast.error(resumeProblem(f)); e.target.value = ''; setResume(null); return; }
                    setResume(f);
                  }} className="text-sm" />
                  {resume && <p className="text-xs text-amber-800 font-semibold mt-1.5">✓ {resume.name} · {(resume.size / 1024).toFixed(0)} KB</p>}
                  <p className="text-[11px] text-amber-900 mt-1.5">Add it now, or on the lead page within {resumeMinutes} minutes (by the meeting, if it is sooner). After that the Lead Managers are told it is late.</p>
                </>
              )}
            </div>
          </div>
        )}

        {outcome === 'Converted' && (lead.pitched ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className={labelCls} htmlFor="c-amt">Amount paid (₹)</label><input id="c-amt" type="number" min="1" value={amount} onChange={e => setAmount(e.target.value)} className={inputCls} /></div>
            <div>
              <label className={labelCls} htmlFor="c-shot">Payment screenshot (optional)</label>
              <input id="c-shot" type="file" accept="image/*" onChange={e => setShot(e.target.files?.[0] || null)} className="text-sm" />
            </div>
            <p className="sm:col-span-2 text-xs text-gray-500 bg-green-50 rounded-xl p-3">This makes their payment record, credited to {lead.ownerName || 'the salesperson'}. They then show in Unassigned Leads for a mentor and batch, and get their student login.</p>
          </div>
        ) : (
          <p className="text-sm text-orange-700 bg-orange-50 rounded-xl p-3">Send the brochure first (Brochure Sent, with the call recording), then mark them Converted.</p>
        ))}

        {outcome && outcome !== 'Brochure Sent' && outcome !== 'Coordinator Meeting' && (
          <div>
            <label className={labelCls} htmlFor="crm-note">Note</label>
            <textarea id="crm-note" rows={2} value={note} onChange={e => setNote(e.target.value)} className={`${inputCls} resize-none`} placeholder="Anything to remember for the next call" />
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={save} disabled={!!saving || (!missed && (!outcome || (outcome === 'Converted' && !lead.pitched)))}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={purpleBg}>
            {saving || (missed ? 'Save: move to No Response' : 'Save call')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LogCallModal;
